/* =========================================================
   SIC69 — PDP helpers (kept separate from product.js)
   Contents:
   1) Remove standalone "Price" label (scoped + safe)
   2) Gucci-style accordion motion (robust)
   3) Encrypted/scramble text (homepage/sections via data-sic-encrypted)
      - UPDATED: only the scrambling digits get a class (.sic-scramble-digit)
      - Speed/metrics unchanged (uses your same revealDelay/flipDelay logic)
   4) Editor dropdown: MM-style drag highlight + RELEASE selects + tap-outside close
      (no fullscreen overlay)
   ========================================================= */

(function () {
  if (window.__SIC_JS_INIT__) return;
  window.__SIC_JS_INIT__ = true;

  /* ===========================
     1) Remove standalone "Price"
     =========================== */
  function removeStandalonePriceLabel() {
    // Only run on product templates
    var pageRoot =
      document.querySelector(".template-product") ||
      document.querySelector('[class*="template-product"]');

    if (!pageRoot) return;

    // Scope to likely PDP meta/right column areas only
    var scope =
      pageRoot.querySelector(".product-right") ||
      pageRoot.querySelector(".product-single__meta") ||
      pageRoot;

    // Target elements that are commonly used as "label" nodes in themes
    var candidates = scope.querySelectorAll(
      "label, .label, .field__label, .product__label, .product-single__meta *"
    );

    candidates.forEach(function (el) {
      if (!el) return;

      // Avoid containers that contain other elements (prevents collateral damage)
      if (el.children && el.children.length) return;

      var txt = (el.textContent || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

      if (txt === "price") {
        el.style.display = "none";
        el.setAttribute("data-sic-hidden", "price-label");
      }
    });
  }

  function initRemovePriceLabel() {
    removeStandalonePriceLabel();

    // small retry window for late renders
    var tries = 0;
    var t = setInterval(function () {
      removeStandalonePriceLabel();
      tries++;
      if (tries > 30) clearInterval(t); // ~4.5s
    }, 150);

    // observe DOM changes in product template only
    var root =
      document.querySelector(".template-product") ||
      document.querySelector('[class*="template-product"]') ||
      document.body;

    var obs = new MutationObserver(function () {
      removeStandalonePriceLabel();
    });

    obs.observe(root, { childList: true, subtree: true });
  }

  /* ==========================================
     2) Gucci-style accordion motion (robust)
     ========================================== */
  function initSicAccordions() {
    var items = Array.prototype.slice.call(
      document.querySelectorAll("details.sic-accordion")
    );
    if (!items.length) return;

    document.documentElement.classList.add("sic-accordion-js");

    const SIC_ACCORDION_SPEED = 580; // MASTER SPEED CONTROL — match CSS roughly

    function getParts(detailsEl) {
      return {
        summary: detailsEl.querySelector("summary"),
        panel: detailsEl.querySelector(".sic-accordion__panel"),
        inner: detailsEl.querySelector(".sic-accordion__panel-inner"),
      };
    }

    function setClosed(panel) {
      if (panel) panel.style.height = "0px";
    }

    function setAuto(panel) {
      if (panel) panel.style.height = "auto";
    }

    function openDetails(detailsEl) {
      if (detailsEl.dataset.sicBusy === "1") return;
      detailsEl.dataset.sicBusy = "1";

      var p = getParts(detailsEl);
      if (!p.panel || !p.inner) {
        detailsEl.dataset.sicBusy = "0";
        return;
      }

      detailsEl.classList.add("is-opening");
      detailsEl.setAttribute("open", "");

      p.panel.style.height = "0px";
      p.panel.offsetHeight; // reflow
      p.panel.style.height = p.inner.scrollHeight + "px";

      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        setAuto(p.panel);
        detailsEl.classList.remove("is-opening");
        detailsEl.dataset.sicBusy = "0";
      };

      var onEnd = function (e) {
        if (e.propertyName !== "height") return;
        p.panel.removeEventListener("transitionend", onEnd);
        finish();
      };

      p.panel.addEventListener("transitionend", onEnd);
      setTimeout(finish, SIC_ACCORDION_SPEED + 80);
    }

    function closeDetails(detailsEl) {
      if (detailsEl.dataset.sicBusy === "1") return;
      detailsEl.dataset.sicBusy = "1";

      var p = getParts(detailsEl);
      if (!p.panel || !p.inner) {
        detailsEl.dataset.sicBusy = "0";
        return;
      }

      var start = p.panel.getBoundingClientRect().height;
      p.panel.style.height = start + "px";
      p.panel.offsetHeight; // reflow

      /* remove open immediately so label/icon state changes instantly */
      detailsEl.removeAttribute("open");

      p.panel.offsetHeight; // reflow
      p.panel.style.height = "0px";

      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        setClosed(p.panel);
        detailsEl.dataset.sicBusy = "0";
      };

      var onEnd = function (e) {
        if (e.propertyName !== "height") return;
        p.panel.removeEventListener("transitionend", onEnd);
        finish();
      };

      p.panel.addEventListener("transitionend", onEnd);
      setTimeout(finish, SIC_ACCORDION_SPEED + 80);
    }

    function bindInnerAutoResize(detailsEl) {
      var p = getParts(detailsEl);
      if (!p.inner || !p.panel) return;

      var imgs = p.inner.querySelectorAll("img");
      imgs.forEach(function (img) {
        if (img.complete) return;
        img.addEventListener("load", function () {
          if (!detailsEl.hasAttribute("open")) return;
          if (p.panel.style.height && p.panel.style.height !== "auto") {
            p.panel.style.height = p.inner.scrollHeight + "px";
          }
        });
      });

      if ("ResizeObserver" in window) {
        var ro = new ResizeObserver(function () {
          if (!detailsEl.hasAttribute("open")) return;
          if (p.panel.style.height && p.panel.style.height !== "auto") {
            p.panel.style.height = p.inner.scrollHeight + "px";
          }
        });
        ro.observe(p.inner);
      }
    }

    items.forEach(function (detailsEl) {
      var p = getParts(detailsEl);
      if (!p.summary || !p.panel || !p.inner) return;

      if (detailsEl.hasAttribute("open")) setAuto(p.panel);
      else setClosed(p.panel);

      bindInnerAutoResize(detailsEl);

      p.summary.addEventListener("click", function (e) {
        e.preventDefault();
        if (detailsEl.hasAttribute("open")) closeDetails(detailsEl);
        else openDetails(detailsEl);
      });
    });

    window.addEventListener(
      "resize",
      function () {
        items.forEach(function (detailsEl) {
          if (!detailsEl.hasAttribute("open")) return;
          var p = getParts(detailsEl);
          if (!p.panel || !p.inner) return;
          if (p.panel.style.height && p.panel.style.height !== "auto") {
            p.panel.style.height = p.inner.scrollHeight + "px";
          }
        });
      },
      { passive: true }
    );
  }

 /* ==========================================
   3) Encrypted / scramble text (in-view)
   CTA loop: repeat CTA only every 3s
   ========================================== */
function initSicEncryptedText() {
  var targets = Array.prototype.slice.call(
    document.querySelectorAll("[data-sic-encrypted]")
  );
  if (!targets.length) return;

  var CTA_WORDS = ["create", "configure", "explore", "discover", "archive", "contact"];

function isCtaLink(link) {
  if (!link) return false;

  /* Future-proof: any linked CTA inside Home About text should loop */
  if (link.closest && link.closest(".home-about-text")) {
    return true;
  }

  var txt = (link.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  return CTA_WORDS.indexOf(txt) > -1;
}

  function isHomeAboutText(root) {
    return !!(
      root &&
      root.classList &&
      root.classList.contains("home-about-text")
    );
  }

  function getTextNodes(root, mode) {
    var isIndexPage =
      document.body && document.body.classList.contains("template-index");

    var isHomeAbout = isHomeAboutText(root);

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || !node.nodeValue.trim().length) {
          return NodeFilter.FILTER_REJECT;
        }

        var parent = node.parentElement;
        if (!parent) {
          return NodeFilter.FILTER_REJECT;
        }

        /* CTA-only mode for repeat loop */
        if (mode === "cta-only") {
          var ctaLink = parent.closest("a");
          return ctaLink && isCtaLink(ctaLink)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }

       /* Home page invitation copy:
   scramble section titles + linked CTAs only.
   Bold editorial text stays static. */
if (isHomeAbout) {
  var inTitle =
    !!parent.closest("h1, h2, h3, h4, h5, h6");

  var link = parent.closest("a");
  var inCTA = !!(link && isCtaLink(link));

  return (inTitle || inCTA)
    ? NodeFilter.FILTER_ACCEPT
    : NodeFilter.FILTER_REJECT;
}

        /* Other homepage button-like UI stays normal unless explicitly tagged */
        if (
          isIndexPage &&
          parent.closest(
            '.button, a.button, a[class*="button"], .btn, .CreateButton, .buy-now-button, button, [role="button"], [data-sic-no-encrypt]'
          )
        ) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    var out = [];
    while (walker.nextNode()) out.push(walker.currentNode);
    return out;
  }

  function randChar(charset) {
    return charset[(Math.random() * charset.length) | 0];
  }

  function getPrepKeys(mode) {
    return mode === "cta-only"
      ? {
          prepared: "__sicGlyphPreparedCta",
          original: "__sicOriginalHTMLCta",
          spans: "__sicGlyphSpansCta",
        }
      : {
          prepared: "__sicGlyphPrepared",
          original: "__sicOriginalHTML",
          spans: "__sicGlyphSpans",
        };
  }

  function prepareGlyphSpans(el, mode) {
    var keys = getPrepKeys(mode);
    if (el[keys.prepared]) return;

    el[keys.prepared] = true;
    el[keys.original] = el.innerHTML;

    var nodes = getTextNodes(el, mode);
    var spans = [];

    nodes.forEach(function (node) {
      var s = node.nodeValue || "";
      if (!s.length) return;

      var frag = document.createDocumentFragment();

      for (var i = 0; i < s.length; i++) {
        var ch = s[i];
        var sp = document.createElement("span");
        sp.textContent = ch;
        sp.dataset.sicOrig = ch;

        if (ch === " " || ch === "\n" || ch === "\t") {
          sp.dataset.sicSpace = "1";
        }

        frag.appendChild(sp);
        spans.push(sp);
      }

      node.parentNode.replaceChild(frag, node);
    });

    el[keys.spans] = spans;
  }

  function restoreOriginal(el, mode) {
    var keys = getPrepKeys(mode);

    if (el[keys.original] != null) {
      el.innerHTML = el[keys.original];
    }

    el[keys.spans] = null;
    el[keys.prepared] = false;
    el[keys.original] = null;
  }

  function run(el, opts) {
    opts = opts || {};

    var mode = opts.mode || "default";
    var once = !!opts.once;
    var onComplete = opts.onComplete || null;
    var doneKey = mode === "cta-only" ? "sicEncryptedCtaDone" : "sicEncryptedDone";

    if (el.dataset.sicEncrypting === "1") return;
    if (once && el.dataset[doneKey] === "1") return;

    if (once) {
      el.dataset[doneKey] = "1";
    }

    el.dataset.sicEncrypting = "1";
    el.classList.add("sic-encrypting");

    var revealDelay = parseInt(el.getAttribute("data-sic-reveal"), 20) || 80;
    var flipDelay = parseInt(el.getAttribute("data-sic-flip"), 10) || 100;
    var charset = el.getAttribute("data-sic-charset") || "0123456789";

    prepareGlyphSpans(el, mode);

    var keys = getPrepKeys(mode);
    var spans = el[keys.spans] || [];
    if (!spans.length) {
      el.classList.remove("sic-encrypting");
      el.dataset.sicEncrypting = "0";
      if (typeof onComplete === "function") onComplete();
      return;
    }

    var startTime = performance.now();
    var lastFlipT = 0;

    function tick(now) {
      var elapsed = now - startTime;
      var revealed = Math.floor(elapsed / revealDelay);

      var doFlip = now - lastFlipT >= flipDelay;
      if (doFlip) lastFlipT = now;

      for (var g = 0; g < spans.length; g++) {
        var sp = spans[g];
        var orig = sp.dataset.sicOrig || sp.textContent;

        if (sp.dataset.sicSpace === "1") {
          sp.textContent = orig;
          sp.classList.remove("sic-scramble-digit");
          continue;
        }

        if (g <= revealed) {
          sp.textContent = orig;
          sp.classList.remove("sic-scramble-digit");
        } else if (doFlip) {
          sp.textContent = randChar(charset);
          sp.classList.add("sic-scramble-digit");
        }
      }

      if (revealed >= spans.length) {
        restoreOriginal(el, mode);
        el.classList.remove("sic-encrypting");
        el.dataset.sicEncrypting = "0";
        if (typeof onComplete === "function") onComplete();
        return;
      }

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  function startCtaLoop(root) {
    if (!isHomeAboutText(root)) return;
    if (root.__sicCtaLoopStarted) return;

    root.__sicCtaLoopStarted = true;

   function scheduleLink(link) {
  function getLoopDelay() {
    var txt = (link.textContent || "")
      .replace(/\s+/g, " ")
      .trim();

    /*
      Short CTAs keep the current rhythm.
      Longer CTAs get a longer rest so they do not feel restless.
    */
    if (txt.length > 22) return 6500;
    if (txt.length > 14) return 5200;

    return 3000;
  }

  function loop() {
    if (!document.body.contains(link)) return;

    if (!document.hidden) {
      run(link, { mode: "cta-only" });
    }

    link.__sicCtaTimer = setTimeout(loop, getLoopDelay());
  }

  link.__sicCtaTimer = setTimeout(loop, getLoopDelay());
}

    var links = Array.prototype.slice.call(root.querySelectorAll("a"))
      .filter(isCtaLink);

    links.forEach(scheduleLink);
  }

  function handleTarget(el) {
    run(el, {
      once: true,
      onComplete: function () {
        startCtaLoop(el);
      },
    });
  }

  if ("IntersectionObserver" in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            handleTarget(entry.target);
          }
        });
      },
      { threshold: 0.35 }
    );

    targets.forEach(function (el) {
      io.observe(el);
    });
  } else {
    targets.forEach(handleTarget);
  }
}

function startSicEncryptedTextWhenReady() {
  var didRun = false;

  function run() {
    if (didRun) return;
    didRun = true;
    initSicEncryptedText();
  }

  if (window.__sicScrollRestored || !window.__sicAwaitScrollRestore) {
    run();
    return;
  }

  window.addEventListener("sic:scroll-restored", run, { once: true });

  // fallback so scramble still runs even if restore event is missed
  setTimeout(run, 4500);
}

  /* ==========================================
     4) Editor dropdown: MM-style finger highlight + RELEASE selects + closes
     ========================================== */
  function initSicDropdownScrollContainment() {
  if (window.__SIC_DROPDOWN_CONTAIN__) return;
  window.__SIC_DROPDOWN_CONTAIN__ = true;

  var backdropId = "sicDropdownBackdrop";
  var tapShieldId = "sicDropdownTapShield";
  var openDetails = null;
  var activePanel = null;

  var hoverBtn = null;
  var ignoreClickUntil = 0;
  var ghostBlockUntil = 0;
  var lastY = 0;

  var panelButtons = [];
  var boundPanel = null;

  function normTarget(t) {
    return t && t.nodeType === 3 ? t.parentElement : t;
  }

  function shouldBlockGhostEvent() {
    return Date.now() < Math.max(ignoreClickUntil, ghostBlockUntil);
  }

  function blockGhostEvent(e) {
    if (!e || !e.isTrusted || !shouldBlockGhostEvent()) return false;

    if (typeof e.preventDefault === "function") e.preventDefault();
    if (typeof e.stopPropagation === "function") e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") {
      e.stopImmediatePropagation();
    }
    return true;
  }

  function targetIsCipherInput(t) {
    return !!(
      t &&
      t.closest &&
      t.closest("#cipherInputContainer .cipherInput, #cipherInputContainer input, #cipherInputContainer textarea")
    );
  }

  function setCurrent(detailsEl, txt) {
    var cur = detailsEl.querySelector(".editor-dropdown__current");
    if (cur) cur.textContent = (txt || "").replace(/\s+/g, " ").trim();
  }

  function syncCurrentFromActive(detailsEl) {
    var active =
      detailsEl.querySelector(".editor-dropdown__panel .variantSwatch.active") ||
      detailsEl.querySelector(".editor-dropdown__panel .variantSwatch");
    if (active) setCurrent(detailsEl, active.textContent);
  }

  document.querySelectorAll("details.editor-dropdown").forEach(syncCurrentFromActive);

  function clearHover() {
    if (hoverBtn) hoverBtn.classList.remove("sic-touch-hover");
    hoverBtn = null;
  }

  function setHover(btn) {
    if (!btn || btn === hoverBtn) return;
    clearHover();
    hoverBtn = btn;
    hoverBtn.classList.add("sic-touch-hover");
  }

  function cacheButtons() {
    panelButtons = activePanel
      ? Array.prototype.slice.call(
          activePanel.querySelectorAll(".variantSwatch.js-product-option")
        )
      : [];
  }

  function ensureBackdrop() {
    var el = document.getElementById(backdropId);
    if (el) return el;

    el = document.createElement("div");
    el.id = backdropId;
    el.style.position = "fixed";
    el.style.inset = "0";
    el.style.background = "transparent";
    el.style.zIndex = "9000"; // below dropdown (9999), above page
    el.style.display = "none";

    el.addEventListener("click", closeAll);
    el.addEventListener("touchmove", function (e) { e.preventDefault(); }, { passive: false });
    el.addEventListener("wheel", function (e) { e.preventDefault(); }, { passive: false });

    document.body.appendChild(el);
    return el;
  }

  function ensureTapShield() {
    var el = document.getElementById(tapShieldId);
    if (el) return el;

    el = document.createElement("div");
    el.id = tapShieldId;
    el.style.position = "fixed";
    el.style.inset = "0";
    el.style.background = "transparent";
    el.style.zIndex = "9001"; // below dropdown, above page
    el.style.display = "none";
    el.style.touchAction = "none";

    ["pointerdown", "pointerup", "mousedown", "mouseup", "click", "touchstart", "touchend"].forEach(function (type) {
      el.addEventListener(type, function (e) {
        if (shouldBlockGhostEvent()) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    });

    el.addEventListener("touchmove", function (e) {
      if (shouldBlockGhostEvent()) e.preventDefault();
    }, { passive: false });

    el.addEventListener("wheel", function (e) {
      if (shouldBlockGhostEvent()) e.preventDefault();
    }, { passive: false });

    document.body.appendChild(el);
    return el;
  }

  function openBackdrop() {
    ensureBackdrop().style.display = "block";
    document.documentElement.classList.add("sic-dd-open");
  }

  function closeBackdrop() {
    var b = document.getElementById(backdropId);
    if (b) b.style.display = "none";
    document.documentElement.classList.remove("sic-dd-open");
  }

  function openTapShield(ms) {
    var el = ensureTapShield();
    ghostBlockUntil = Date.now() + ms;
    el.style.display = "block";

    clearTimeout(openTapShield._timer);
    openTapShield._timer = setTimeout(function () {
      el.style.display = "none";
    }, ms);
  }

  function closeAll() {
    document.querySelectorAll("details.editor-dropdown[open]").forEach(function (d) {
      d.removeAttribute("open");
    });
    openDetails = null;
    activePanel = null;
    panelButtons = [];
    clearHover();
    unbindPanel();
    closeBackdrop();
  }

  function closeOthers(current) {
    document.querySelectorAll("details.editor-dropdown[open]").forEach(function (d) {
      if (d !== current) d.removeAttribute("open");
    });
  }

  function findClosestButtonByY(y) {
    if (!panelButtons.length) return null;

    var best = null;
    var bestDist = Infinity;

    for (var i = 0; i < panelButtons.length; i++) {
      var btn = panelButtons[i];
      var r = btn.getBoundingClientRect();
      var cy = (r.top + r.bottom) / 2;
      var dist = Math.abs(y - cy);
      if (dist < bestDist) {
        bestDist = dist;
        best = btn;
      }
    }
    return best;
  }

  function onPanelStart(e) {
    if (!activePanel || !e.touches || !e.touches[0]) return;
    lastY = e.touches[0].clientY;

    var btn = findClosestButtonByY(lastY);
    if (btn) setHover(btn);
  }

  function onPanelMove(e) {
    if (!activePanel || !e.touches || !e.touches[0]) return;

    var y = e.touches[0].clientY;
    var btn = findClosestButtonByY(y);
    if (btn) setHover(btn);

    // stop scroll chaining at edges (page stays locked)
    var dyStep = y - lastY;
    lastY = y;

    var atTop = activePanel.scrollTop <= 0;
    var atBottom =
      activePanel.scrollTop + activePanel.clientHeight >= activePanel.scrollHeight - 1;

    if ((atTop && dyStep > 0) || (atBottom && dyStep < 0)) {
      e.preventDefault();
    }
  }

  function onPanelEnd() {
    if (!openDetails || !activePanel) return;

    if (hoverBtn) {
      var chosenBtn = hoverBtn;

      ignoreClickUntil = Date.now() + 900;
      openTapShield(750);

      setTimeout(function () {
        chosenBtn.click(); // selects
        if (openDetails) openDetails.removeAttribute("open"); // closes
      }, 0);
    }

    setTimeout(clearHover, 0);
  }

  function bindPanel(panel) {
    if (!panel || boundPanel === panel) return;
    unbindPanel();
    boundPanel = panel;

    boundPanel.addEventListener("touchstart", onPanelStart, { passive: true });
    boundPanel.addEventListener("touchmove", onPanelMove, { passive: false });
    boundPanel.addEventListener("touchend", onPanelEnd, { passive: true });
  }

  function unbindPanel() {
    if (!boundPanel) return;
    boundPanel.removeEventListener("touchstart", onPanelStart);
    boundPanel.removeEventListener("touchmove", onPanelMove);
    boundPanel.removeEventListener("touchend", onPanelEnd);
    boundPanel = null;
  }

  // KEY CHANGE: delegate toggle so it works even if the dropdown DOM is replaced (127 case)
  document.addEventListener(
    "toggle",
    function (e) {
      var d = e.target;
      if (!d || d.tagName !== "DETAILS" || !d.classList.contains("editor-dropdown")) return;

      if (d.open) {
        closeOthers(d);

        openDetails = d;
        activePanel = d.querySelector(".editor-dropdown__panel") || null;

        cacheButtons();
        bindPanel(activePanel);

        openBackdrop();
        syncCurrentFromActive(d);
      } else {
        if (openDetails === d) {
          openDetails = null;
          activePanel = null;
          panelButtons = [];
          clearHover();
          unbindPanel();
          closeBackdrop();
        }
      }
    },
    true
  );

  ["pointerup", "touchend"].forEach(function (type) {
    document.addEventListener(
      type,
      function (e) {
        blockGhostEvent(e);
      },
      true
    );
  });

  document.addEventListener(
    "focusin",
    function (e) {
      var t = normTarget(e.target);
      if (!t || !targetIsCipherInput(t)) return;
      if (!shouldBlockGhostEvent()) return;

      try { t.blur(); } catch (err) {}
    },
    true
  );

  // swallow the follow-up trusted click so it cannot hit the layer underneath
  document.addEventListener(
    "click",
    function (e) {
      if (blockGhostEvent(e)) return;

      var t = normTarget(e.target);
      if (!t || !t.closest) return;

      var sw = t.closest(".editor-dropdown__panel .variantSwatch.js-product-option");
      if (!sw) return;

      var d = sw.closest("details.editor-dropdown");
      if (!d) return;

      setCurrent(d, sw.textContent);
      setTimeout(function () { syncCurrentFromActive(d); }, 60);

      clearHover();
      d.removeAttribute("open");
    },
    true
  );

  window.addEventListener("pagehide", closeAll);
}

function initSicGlobalHeaderFade() {
  if (window.__SIC_GLOBAL_HEADER_FADE__) return;
  window.__SIC_GLOBAL_HEADER_FADE__ = true;

  var ACTION_DELAY = 360;
  var DRAWER_RESET_DELAY = 420;
  var DRAWER_RETRY_DELAY = 180;
  var FADE_CLASS = "is-fading";

  var HOME_SELECTOR = "[data-sic-header-home='1']";
  var DRAWER_SELECTOR = "[data-sic-header-drawer='1']";
  var TRIGGER_SELECTOR = HOME_SELECTOR + ", " + DRAWER_SELECTOR;

  function getTrigger(target) {
    if (!target || !target.closest) return null;
    return target.closest(TRIGGER_SELECTOR);
  }

  function isHomeTrigger(trigger) {
    return !!(trigger && trigger.matches && trigger.matches(HOME_SELECTOR));
  }

  function isDrawerTrigger(trigger) {
    return !!(trigger && trigger.matches && trigger.matches(DRAWER_SELECTOR));
  }

  function isFading(trigger) {
    return !!(
      trigger &&
      trigger.getAttribute &&
      trigger.getAttribute("data-sic-fading") === "1"
    );
  }

  function startFade(trigger) {
    if (!trigger) return;
    trigger.setAttribute("data-sic-fading", "1");
    trigger.classList.add(FADE_CLASS);
  }

  function clearFade(trigger) {
    if (!trigger) return;
    trigger.classList.remove(FADE_CLASS);
    trigger.removeAttribute("data-sic-fading");
  }

  function goHome(trigger) {
    var href = (trigger && trigger.getAttribute("href")) || "/";
    window.location.href = href;
  }

  function getNativeDrawerTrigger(trigger) {
    if (!trigger || !trigger.closest) return null;

    var side = trigger.closest(".sic-header__side--right");
    if (side) {
      var nativeBtn = side.querySelector("[data-sic-header-drawer-native='1']");
      if (nativeBtn) return nativeBtn;
    }

    return document.querySelector("[data-sic-header-drawer-native='1']");
  }

  function resetNativeDrawerTrigger(nativeTrigger) {
    if (!nativeTrigger) return;

    try { nativeTrigger.blur(); } catch (e) {}

    nativeTrigger.setAttribute("aria-expanded", "false");
    nativeTrigger.classList.remove("is-active", "active", "is-open", "open", "opened");
    nativeTrigger.removeAttribute("data-sic-fade-busy");
  }

  function fireNativeDrawerTrigger(nativeTrigger) {
    if (!nativeTrigger) return;

    try {
      nativeTrigger.click();
      return;
    } catch (e) {}

    try {
      nativeTrigger.dispatchEvent(
        new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true
        })
      );
    } catch (err) {}
  }

  function drawerLooksOpen() {
    var drawer = document.getElementById("NavDrawer");
    var html = document.documentElement;
    var body = document.body;

    if (drawer) {
      var ariaHidden = drawer.getAttribute("aria-hidden");
      if (ariaHidden === "false") return true;

      if (
        drawer.classList.contains("is-open") ||
        drawer.classList.contains("open") ||
        drawer.classList.contains("active") ||
        drawer.classList.contains("drawer--is-open") ||
        drawer.classList.contains("drawer-open")
      ) {
        return true;
      }
    }

    var htmlClass = (html && html.className) || "";
    var bodyClass = (body && body.className) || "";
    var openStatePattern = /(drawer-open|drawer--open|js-drawer-open|is-moved-by-drawer|nav-open)/i;

    return openStatePattern.test(htmlClass) || openStatePattern.test(bodyClass);
  }

  function openDrawer(trigger) {
    var nativeTrigger = getNativeDrawerTrigger(trigger);

    if (!nativeTrigger) {
      clearFade(trigger);
      return;
    }

    resetNativeDrawerTrigger(nativeTrigger);
    fireNativeDrawerTrigger(nativeTrigger);

    window.setTimeout(function () {
      if (!drawerLooksOpen()) {
        resetNativeDrawerTrigger(nativeTrigger);
        fireNativeDrawerTrigger(nativeTrigger);
      }
    }, DRAWER_RETRY_DELAY);

    window.setTimeout(function () {
      clearFade(trigger);
    }, DRAWER_RESET_DELAY);
  }

  function blockEarly(event) {
    var trigger = getTrigger(event.target);
    if (!trigger) return;

    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }
  }

  ["pointerdown", "mousedown", "touchstart"].forEach(function (type) {
    document.addEventListener(type, blockEarly, true);
  });

  document.addEventListener(
    "click",
    function (event) {
      var trigger = getTrigger(event.target);
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }

      if (isFading(trigger)) return;

      startFade(trigger);

      window.setTimeout(function () {
        if (isHomeTrigger(trigger)) {
          goHome(trigger);
          return;
        }

        if (isDrawerTrigger(trigger)) {
          openDrawer(trigger);
          return;
        }

        clearFade(trigger);
      }, ACTION_DELAY);
    },
    true
  );
}

function initSicProductKeyboardVerticalNudge() {
  if (window.__SIC_PRODUCT_KEYBOARD_VERTICAL_NUDGE__) return;
  window.__SIC_PRODUCT_KEYBOARD_VERTICAL_NUDGE__ = true;

  var pageRoot =
    document.querySelector(".template-product") ||
    document.querySelector('[class*="template-product"]');

  if (!pageRoot || !window.visualViewport) return;

  var activeInput = null;
  var timer = null;

  var DESIRED_INPUT_TOP_RATIO = 0.76; /* bigger = lower on screen, more image stays visible */
  var KEYBOARD_OPEN_DELTA = 120;

  function isMobileProduct() {
    return window.matchMedia("(max-width: 989px)").matches;
  }

  function keyboardLooksOpen() {
    return (window.innerHeight - window.visualViewport.height) > KEYBOARD_OPEN_DELTA;
  }

  function getScrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  function correctVerticalPosition() {
    if (!activeInput) return;
    if (!isMobileProduct()) return;
    if (!keyboardLooksOpen()) return;

    var rect = activeInput.getBoundingClientRect();
    var visibleHeight = window.visualViewport.height;
    var desiredTop = Math.round(visibleHeight * DESIRED_INPUT_TOP_RATIO);
    var delta = rect.top - desiredTop;

    if (Math.abs(delta) < 4) return;

    window.scrollTo(0, getScrollY() + delta);
  }

  function queueCorrection() {
    if (!activeInput) return;

    clearTimeout(timer);

    timer = setTimeout(function () {
      correctVerticalPosition();

      setTimeout(function () {
        correctVerticalPosition();
      }, 120);

      setTimeout(function () {
        correctVerticalPosition();
      }, 240);
    }, 60);
  }

  Array.prototype.forEach.call(
    pageRoot.querySelectorAll("#cipherInputContainer .cipherInput"),
    function (input) {
      input.addEventListener("focus", function () {
        if (!isMobileProduct()) return;
        activeInput = input;
        queueCorrection();
      }, true);

      input.addEventListener("blur", function () {
        activeInput = null;
        clearTimeout(timer);
      }, true);
    }
  );

  window.visualViewport.addEventListener("resize", queueCorrection);
  window.addEventListener("orientationchange", queueCorrection);
}

/* ==========================================
   5) Home About image CTA from section text link
   Uses the first link inside .home-about-text as the image CTA.
   Tap opens link, drag/swipe does not.
   ========================================== */
function initSicImageCtas() {
  if (window.__SIC_IMAGE_CTAS__) return;
  window.__SIC_IMAGE_CTAS__ = true;

  var TAP_MOVE_LIMIT = 12;
  var TAP_TIME_LIMIT = 700;

  function bindImageCta(slider, url) {
    if (!slider || !url || slider.dataset.sicImageCtaBound === "1") return;

    slider.dataset.sicImageCtaBound = "1";
    slider.dataset.sicImageCta = url;
    slider.style.cursor = "pointer";

    var startX = 0;
    var startY = 0;
    var startTime = 0;
    var moved = false;
    var validStart = false;

    slider.addEventListener(
      "pointerdown",
      function (event) {
        if (event.button != null && event.button !== 0) return;

        startX = event.clientX;
        startY = event.clientY;
        startTime = Date.now();
        moved = false;
        validStart = true;
      },
      { passive: true }
    );

    slider.addEventListener(
      "pointermove",
      function (event) {
        if (!validStart) return;

        var dx = Math.abs(event.clientX - startX);
        var dy = Math.abs(event.clientY - startY);

        if (dx > TAP_MOVE_LIMIT || dy > TAP_MOVE_LIMIT) {
          moved = true;
        }
      },
      { passive: true }
    );

    slider.addEventListener(
      "pointerup",
      function (event) {
        if (!validStart) return;

        var elapsed = Date.now() - startTime;
        var dx = Math.abs(event.clientX - startX);
        var dy = Math.abs(event.clientY - startY);

        var isTap =
          !moved &&
          dx <= TAP_MOVE_LIMIT &&
          dy <= TAP_MOVE_LIMIT &&
          elapsed <= TAP_TIME_LIMIT;

        validStart = false;

        if (!isTap) return;

        var link = slider.dataset.sicImageCta;
        if (!link) return;

        window.location.href = link;
      },
      { passive: true }
    );

    slider.addEventListener(
      "pointercancel",
      function () {
        validStart = false;
        moved = false;
      },
      { passive: true }
    );

    slider.setAttribute("role", "link");
    slider.setAttribute("tabindex", "0");

    slider.addEventListener("keydown", function (event) {
      if (event.key !== "Enter" && event.key !== " ") return;

      var link = slider.dataset.sicImageCta;
      if (!link) return;

      event.preventDefault();
      window.location.href = link;
    });
  }

  function scan(root) {
    Array.prototype.forEach.call(
      (root || document).querySelectorAll(".home-section-about"),
      function (section) {
        var slider = section.querySelector(".home-photo-slider");
        var textLink = section.querySelector(".home-about-text a[href]");

        if (!slider || !textLink) return;

        var url = textLink.getAttribute("href");
        if (!url) return;

        bindImageCta(slider, url);
      }
    );
  }

  scan(document);

  document.addEventListener("shopify:section:load", function (event) {
    scan(event.target || document);
  });
}

function start() {
  initRemovePriceLabel();
  initSicAccordions();
  startSicEncryptedTextWhenReady();
  initSicDropdownScrollContainment();
  initSicGlobalHeaderFade();
  initSicProductKeyboardVerticalNudge();
  initSicImageCtas();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start);
} else {
  start();
}
})();
/* =========================================================
   SIC69 — SUMMARY BOX OPTION 2
   dark fill pulse after typing pause
   ========================================================= */
(function () {
  var DEBOUNCE_MS = 180;
  var FLASH_MS = 240;

  function normalize(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function bindSummaryPulse(container) {
    var box = container.querySelector('#cipherOutputSum');
    var hidden = container.querySelector('.cipherOutputSumHidden');
    if (!box || box.dataset.sicSumPulseBound === 'true') return;

    box.dataset.sicSumPulseBound = 'true';

    var lastValue = normalize((hidden && hidden.value) || box.textContent);
    var debounceTimer = null;
    var flashTimer = null;

    function currentValue() {
      var hiddenValue = hidden ? normalize(hidden.value) : '';
      var liveValue = normalize(box.textContent);
      return hiddenValue || liveValue;
    }

    function triggerFlash() {
  if (document.documentElement.classList.contains('sic-dark-mode')) {
    box.classList.remove('sic-sum-flash');
    return;
  }

  box.classList.remove('sic-sum-flash');
  void box.offsetWidth;
  box.classList.add('sic-sum-flash');

  clearTimeout(flashTimer);
  flashTimer = setTimeout(function () {
    box.classList.remove('sic-sum-flash');
  }, FLASH_MS);
}

    function queueFlashIfChanged() {
      var nextValue = currentValue();
      if (!nextValue || nextValue === lastValue) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var confirmedValue = currentValue();
        if (!confirmedValue || confirmedValue === lastValue) return;
        lastValue = confirmedValue;
        triggerFlash();
      }, DEBOUNCE_MS);
    }

    if (window.MutationObserver) {
      var observer = new MutationObserver(queueFlashIfChanged);
      observer.observe(box, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    if (hidden) {
      hidden.addEventListener('input', queueFlashIfChanged);
      hidden.addEventListener('change', queueFlashIfChanged);
    }

    setInterval(queueFlashIfChanged, 120);
  }

  function initSummaryPulse(root) {
    (root || document).querySelectorAll('#cipherInputContainer').forEach(bindSummaryPulse);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initSummaryPulse(document);
    });
  } else {
    initSummaryPulse(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initSummaryPulse(event.target || document);
  });
})();

/* =========================================================
   SIC69 — CIPHER LIVE STATE
   whole unit activates while typing
   sum box releases last
   ========================================================= */

/* =========================================================
   EDIT THESE FIRST
   ========================================================= */
(function () {
  var HOLD_MS = 900;
  var ENTER_OUTPUT_MS = 70;
  var ENTER_SUM_MS = 150;

  var EXIT_INPUT_MS = 0;
  var EXIT_OUTPUT_MS = 120;
  var EXIT_SUM_MS = 260;

  function clearCipherLiveTimers($container) {
    var timers = $container.data('sicCipherLiveTimers') || [];
    timers.forEach(clearTimeout);
    $container.data('sicCipherLiveTimers', []);
  }

  function pushCipherLiveTimer($container, timer) {
    var timers = $container.data('sicCipherLiveTimers') || [];
    timers.push(timer);
    $container.data('sicCipherLiveTimers', timers);
  }

  function removeClassLater($container, className, delay) {
    var t = setTimeout(function () {
      $container.removeClass(className);
    }, delay);
    pushCipherLiveTimer($container, t);
  }

  function activateCipherLive($container) {
    if (!$container || !$container.length) return;

    clearCipherLiveTimers($container);

    $container.addClass('sic-cipher-live-input');

    pushCipherLiveTimer($container, setTimeout(function () {
      $container.addClass('sic-cipher-live-output');
    }, ENTER_OUTPUT_MS));

    pushCipherLiveTimer($container, setTimeout(function () {
      $container.addClass('sic-cipher-live-sum');
    }, ENTER_SUM_MS));

    pushCipherLiveTimer($container, setTimeout(function () {
      removeClassLater($container, 'sic-cipher-live-input', EXIT_INPUT_MS);
      removeClassLater($container, 'sic-cipher-live-output', EXIT_OUTPUT_MS);
      removeClassLater($container, 'sic-cipher-live-sum', EXIT_SUM_MS);
    }, HOLD_MS));
  }

  function bindCipherLive(container) {
    var $container = $(container);
    if (!$container.length || $container.data('sicCipherLiveBound')) return;

    $container.data('sicCipherLiveBound', true);

    var $input = $('.cipherInput', $container);
    if (!$input.length) return;

    $input.on('input keydown focus paste', function () {
      activateCipherLive($container);
    });

    $input.on('blur', function () {
      clearCipherLiveTimers($container);
      pushCipherLiveTimer($container, setTimeout(function () {
        removeClassLater($container, 'sic-cipher-live-input', EXIT_INPUT_MS);
        removeClassLater($container, 'sic-cipher-live-output', EXIT_OUTPUT_MS);
        removeClassLater($container, 'sic-cipher-live-sum', EXIT_SUM_MS);
      }, 240));
    });
  }

  function initCipherLive(root) {
    $(root || document).find('#cipherInputContainer').each(function () {
      bindCipherLive(this);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initCipherLive(document);
    });
  } else {
    initCipherLive(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initCipherLive(event.target || document);
  });
})();

/* =========================================================
   SIC69 — CIPHER GUIDE LABELS
   WORDS / CIPHER / CODE
   fires once per idle -> active blank session
   ========================================================= */
(function () {
  if (window.__SIC_CIPHER_GUIDE_LABELS_INIT__) return;
  window.__SIC_CIPHER_GUIDE_LABELS_INIT__ = true;

  var FLASH_CLASS = 'sic-cipher-guide-flash';
  var VALUE_CLASS = 'sic-cipher-has-value';
  var FLASH_TOTAL_MS = 3300;

  function isBlank(input) {
    return !((input.value || '').trim());
  }

  function clearGuide(container) {
    container.classList.remove(FLASH_CLASS);

    if (container.__sicGuideTimer) {
      clearTimeout(container.__sicGuideTimer);
      container.__sicGuideTimer = null;
    }
  }

  function syncValueState(container, input) {
    if (isBlank(input)) {
      container.classList.remove(VALUE_CLASS);
    } else {
      container.classList.add(VALUE_CLASS);
    }
  }

  function triggerGuide(container, input, wrap) {
    if (!container || !input || !wrap) return;
    if (wrap.classList.contains('is-ready-to-wear')) return;
    if (input.disabled) return;
    if (!isBlank(input)) return;
    if (input.__sicGuideShownThisFocus) return;

    input.__sicGuideShownThisFocus = true;

    clearGuide(container);
    void container.offsetWidth;
    container.classList.add(FLASH_CLASS);

    container.__sicGuideTimer = setTimeout(function () {
      container.classList.remove(FLASH_CLASS);
    }, FLASH_TOTAL_MS);
  }

  function bindGuide(container) {
    if (!container || container.dataset.sicGuideBound === 'true') return;

    var input = container.querySelector('.cipherInput');
    var wrap = container.querySelector('.sic-cipher-input-wrap');
    if (!input || !wrap) return;

    container.dataset.sicGuideBound = 'true';
    input.__sicGuideShownThisFocus = false;

    syncValueState(container, input);

    input.addEventListener('focus', function () {
      syncValueState(container, input);
      triggerGuide(container, input, wrap);
    });

    input.addEventListener('input', function () {
      syncValueState(container, input);

      if (!isBlank(input)) {
        clearGuide(container);
      }
    });

    input.addEventListener('blur', function () {
      clearGuide(container);
      input.__sicGuideShownThisFocus = false;
      syncValueState(container, input);
    });
  }

  function initGuides(root) {
    (root || document)
      .querySelectorAll('.template-product #cipherInputContainer')
      .forEach(bindGuide);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initGuides(document);
    });
  } else {
    initGuides(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initGuides(event.target || document);
  });
})();

/* =========================================================
   SIC69 — LIGHT / DARK MODE TOGGLES
   placements:
   1) under centred header logo
   2) drawer footer
   ========================================================= */
(function () {
  if (window.__SIC_MODE_TOGGLE_INIT__) return;
  window.__SIC_MODE_TOGGLE_INIT__ = true;

  var STORAGE_KEY = 'sicThemeMode';

  function getSavedMode() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function saveMode(mode) {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {}
  }

  function getCurrentMode() {
    return document.documentElement.classList.contains('sic-dark-mode')
      ? 'dark'
      : 'light';
  }

  function setMode(mode) {
    var html = document.documentElement;
    var isDark = mode === 'dark';

    html.classList.toggle('sic-dark-mode', isDark);
    html.classList.toggle('sic-light-mode', !isDark);

    saveMode(mode);
    syncAllToggles();
  }

  function toggleMode() {
    setMode(getCurrentMode() === 'dark' ? 'light' : 'dark');
  }

  function syncToggle(el) {
    if (!el) return;

    var mode = getCurrentMode();
    var light = el.querySelector('[data-sic-mode-label="light"]');
    var dark = el.querySelector('[data-sic-mode-label="dark"]');

    el.setAttribute('data-sic-mode-current', mode);
    el.setAttribute('aria-label', mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');

    if (light) light.classList.toggle('is-active', mode === 'light');
    if (dark) dark.classList.toggle('is-active', mode === 'dark');
  }

  function syncAllToggles() {
    document.querySelectorAll('[data-sic-mode-toggle]').forEach(syncToggle);
  }

  function buildToggleMarkup(extraClass) {
    return (
      '<button type="button" class="sic-mode-toggle__button ' + extraClass + '" data-sic-mode-toggle>' +
        '<span class="sic-mode-toggle__label" data-sic-mode-label="light">LIGHT</span>' +
        '<span class="sic-mode-toggle__sep">/</span>' +
        '<span class="sic-mode-toggle__label" data-sic-mode-label="dark">DARK</span>' +
      '</button>'
    );
  }

  function injectHeaderToggle() {
    if (document.querySelector('.sic-mode-toggle--header')) return;

    var wrap = document.createElement('div');
    wrap.className = 'sic-mode-toggle sic-mode-toggle--header';
    wrap.innerHTML = buildToggleMarkup('sic-mode-toggle__button--header');
    document.body.appendChild(wrap);
  }

  function injectDrawerToggle() {
    var drawer = document.getElementById('NavDrawer');
    if (!drawer) return;
    if (drawer.querySelector('.sic-mode-toggle--drawer')) return;

    var mount =
      drawer.querySelector('.sic-drawer-nav') ||
      drawer.querySelector('.drawer__inner') ||
      drawer;

    if (!mount) return;

    var wrap = document.createElement('div');
    wrap.className = 'sic-mode-toggle sic-mode-toggle--drawer';
    wrap.innerHTML = buildToggleMarkup('sic-mode-toggle__button--drawer');
    mount.appendChild(wrap);
  }

  function ensureToggles() {
    injectHeaderToggle();
    injectDrawerToggle();
    syncAllToggles();
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-sic-mode-toggle]');
    if (!btn) return;

    event.preventDefault();
    toggleMode();
  });

  function initModeState() {
    var saved = getSavedMode();
    if (saved === 'dark' || saved === 'light') {
      setMode(saved);
    } else {
      setMode('light');
    }
  }

  function startModeToggles() {
    initModeState();
    ensureToggles();

    var obs = new MutationObserver(function () {
      ensureToggles();
    });

    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startModeToggles);
  } else {
    startModeToggles();
  }
})();